"use client";

/**
 * Collapsible FAQ accordion list for the landing page.
 *
 * Each question still expands in place (native <details>). On top of that, the
 * whole list is capped at INITIAL_COUNT with a "Show all" toggle so the section
 * doesn't run very long with the full question set — the high-intent product
 * questions stay visible, the rest are one click away.
 */

import * as React from "react";

const INITIAL_COUNT = 6;

export function FaqList({ items }: { items: Array<{ q: string; a: string }> }) {
  const [showAll, setShowAll] = React.useState(false);
  const canToggle = items.length > INITIAL_COUNT;
  const visible = showAll || !canToggle ? items : items.slice(0, INITIAL_COUNT);

  return (
    <div>
      <div className="border-t rule">
        {visible.map((f, i) => (
          <details key={f.q} className="group border-b rule">
            <summary className="py-5 flex items-center gap-4">
              <span className="font-mono text-[11px] ink-3 w-8">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="font-display font-medium text-[17px] flex-1 leading-snug">
                {f.q}
              </span>
              <span className="font-mono text-[14px] ink-3 group-open:rotate-45 transition-transform">
                ＋
              </span>
            </summary>
            <div className="pl-12 pr-8 pb-5 text-[14px] ink-2 leading-relaxed max-w-[64ch]">
              {f.a}
            </div>
          </details>
        ))}
      </div>

      {canToggle && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          aria-expanded={showAll}
          className="mt-5 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider ink-2 hover:text-[color:var(--ink)] transition"
        >
          {showAll ? "Show fewer" : `Show all ${items.length} questions`}
          <span
            aria-hidden
            className={"transition-transform " + (showAll ? "rotate-180" : "")}
          >
            ↓
          </span>
        </button>
      )}
    </div>
  );
}
